<!-----------------------------------------------------------------------------
  Copyright (c) 2016 - 2021.                                                  -
  Yega Yazilim ve Bilisim Hizmetleri Dis Tic Ltd Sti (Yega Yazilim)           -
  All Rights Reserved.                                                        -
                                                                              -
  The software and data contained herein are confidential and proprietary     -
  information of Yega Yazilim ("Confidential Information").                   -
                                                                              -
  Users of this software may not disclose such Confidential Information and   -
  shall use it only in accordance with the terms of the license agreement     -
  they entered into with Yega Yazilim.                                        -
                                                                              -
  Author:  Ahmet Dogramaci                                                    -
  Date:    2021-02-25                                                         -
  ----------------------------------------------------------------------------->

<template>
  <div>
    <h2 class="mb-4">{{ templateVersion.attribute_store['short-name'] }}</h2>
    <b-card no-body v-if="compatibilityEnabled && compatibility_tags.length > 0" class="mb-2">
      <b-card-header header-bg-variant="secondary" header-text-variant="light">
        <span class="font-weight-bold">Compatibility Filter</span>
      </b-card-header>
      <b-form inline class="p-1">
        <b-container fluid>
          <b-row>
            <b-col v-for="tag in compatibility_tags" :key="tag" cols="6" sm="4" md="4" lg="3" xl="2">
              <b-form-checkbox
                  v-model="selected_compatibility_tags[tag]"
                  :value="IsSelected.YES"
                  :unchecked-value="IsSelected.NO" @change="handleCompatibilityChange(tag,$event)">
                {{tag}}
              </b-form-checkbox>
            </b-col>
          </b-row>
        </b-container>
      </b-form>
    </b-card>
    <b-form>
      <b-card no-body class="mb-1" v-for="category_node in category_nodes" :key="category_node.id">
        <b-card-header header-bg-variant="info" header-text-variant="light">
          <span class="font-weight-bold">{{ category_node.name }}</span>
        </b-card-header>
        <b-table-lite small
                      borderless
                      striped
                      stacked="lg"
                      :fields="generated_fields"
                      :sort-compare="sortFunc"
                      :items="nodes_by_category_node_id[category_node.id]">
          <template v-slot:cell(action)="data">
            <b-form-checkbox
                v-model="data.item.is_selected"
                :value="IsSelected.YES"
                :unchecked-value="IsSelected.NO"
                :disabled="data.item.isDisabled || data.item.isHidden">
            </b-form-checkbox>
          </template>
        </b-table-lite>
      </b-card>
    </b-form>
  </div>


</template>

<script>
import ConfigurationTemplateVersion from "../../business-objects/configuration-template-version";
import YesNo from "../../enums/yes-no";
import IsSelected from "../../enums/is-selected";
import QuoteProduct from "../../business-objects/quote-product";
import useCurrencyFormatter from "../../compositions/use-currency-formatter";

export default {
  name: "configure-accessory-dialog",
  props: {
    templateVersion: {
      type: ConfigurationTemplateVersion,
      required: true
    },
    quoteProduct:{
      type: QuoteProduct,
      required: true
    },
    pricesList: {
      type: Array,
      required: true
    },
    attributesList: {
      type: Array,
      required: true
    },
    compatibilityEnabled:{
      type:Boolean,
      default:false
    }
  },
  setup(){
    const { formatCurrency } = useCurrencyFormatter();
    return {
      formatCurrency
    };
  },
  created() {
    this.generateTableFields();
  },
  data() {
    return {
      IsSelected: IsSelected,
      generated_fields: [],
      fields: [
        {key: 'action', label: ''},
        {key: 'code', label: 'Product Code'},
        {key: 'name', label: 'Description'}
      ],
      selected_compatibility_tags:{},
      selected_tags_array:[]
    };
  },
  computed: {
    category_nodes() {
      return this.templateVersion.nodes
          .filter((node) => {
            return node.attribute_store['item-type'] === 'category';
          })
          .sort((node1, node2) => {
            if (node1.attribute_store['order'] < node2.attribute_store['order']) {
              return -1;
            }
            if (node1.attribute_store['order'] > node2.attribute_store['order']) {
              return 1;
            }
            return 0;
          });
    },
    nodes_by_category_node_id() {
      return this.category_nodes.reduce((object, category_node) => {
        object[category_node.id] =
            this.templateVersion.nodes.filter((node) => {
              return node.attribute_store['item-type'] === 'content' &&
                  node.attribute_store['category'] === category_node.attribute_store['category'] &&
                  node.attribute_store['hide-on-standard-dialog'] !== YesNo.YES;
            }).filter((node)=>{
              if(this.selected_tags_array.length === 0){
                return true;
              }
              if(node.attribute_store['tags']){
                let tag_array = node.attribute_store['tags'].split(' ');
                let selected= false;
                tag_array.forEach((tag)=>{
                  if(this.selected_compatibility_tags[tag] === IsSelected.YES){
                    selected= true;
                  }
                });
                return selected;
              }
              return false;
            }).sort((node1, node2) => {
              if (node1.attribute_store['order'] < node2.attribute_store['order']) {
                return -1;
              }
              if (node1.attribute_store['order'] > node2.attribute_store['order']) {
                return 1;
              }
              return 0;
            });
        return object;
      }, {});
    },
    compatibility_tags(){
      let tag_set = this.templateVersion.nodes.reduce((tags_set,node)=>{
        if(node.attribute_store['tags']){
          let tag_array = node.attribute_store['tags'].split(' ');
          tag_array.forEach((tag)=>{
            tags_set.add(tag);
          });
        }
        return tags_set;
      },new Set());
      return Array.from(tag_set).sort();
    }
  },
  methods: {
    generateTableFields() {
      this.generated_fields = [...this.fields];
      this.attributesList.forEach((attribute_config) => {
        this.generated_fields.push({
          ...attribute_config,
          formatter: (value, key, item) => {
            return item.attribute_store[key];
          }
        });
      });
      this.pricesList.forEach((price_config) => {
        this.generated_fields.push({
          ...price_config,
          formatter: (value, key, item) => {
            return this.formatCurrency(item.calculated_price_transformations[key]);
          }
        });
      });
    },
    handleCompatibilityChange(tag, status){
      if(status === IsSelected.YES){
        this.selected_tags_array.push(tag);
      }else{
        this.selected_tags_array.splice(this.selected_tags_array.indexOf(tag),1);
      }
    },
    sortFunc(aRow, bRow, key, sortDesc, formatter, compareOptions, compareLocale){
      return toString(aRow.attribute_store.order).localeCompare(toString(bRow.attribute_store.order), compareLocale, compareOptions);

      function toString(val) {
        return '' + val;
      }
    }
  }
}
</script>

<style scoped>

</style>
